//* 부고전송페이지
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  bugoAttributionForCreate,
  bugoAttributionForUpdate,
} from '@service/bugo/utils/bugoAttribution.util';
import { FeventFormUseMode } from '@service/bugo/utils/feventAddEdit.util';
import { createBugo, sendBugo, updateBugo } from '@shared/api/bugo/bugo.controller';
import { Bugo } from '@shared/api/bugo/bugo.interface';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { feventHookUrl } from '@shared/api/fevent/fevent.hook';
import { FeventOne } from '@shared/api/fevent/fevent.interface';
import { updateMember } from '@shared/api/member/member.controller';
import { Member } from '@shared/api/member/member.interface';
import { BottomPopup } from '@shared/components/BottomPopup';
import { Button } from '@shared/components/Button';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { defaultApiMutationOptions } from '@shared/plugIn/apiMutation';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Gender } from '@shared/types';
import { formatFeventDateString } from '@shared/utils/formatDate';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  MemberBugoIFormInput,
  MemberBugoIFormInputs,
  MemberBugoSendFormInputsEnum,
} from '../../interfaces/memberBugoSend.interface';
import { MemberBugoSender } from '../MemberBugoSend/MemberBugoSender';

interface Props {
  fevent: FeventOne;
  useMode?: FeventFormUseMode;
}

const FeventSendBugoForm: FC<Props> = function FeventSendBugoForm({
  fevent,
  useMode,
}: Props) {
  const { bugoRole } = useAuth();
  //*useForm 선언
  const { register, handleSubmit, setValue, control } = useForm<MemberBugoIFormInputs>();
  const [, setSearchParams] = useSearchParams();

  const { userProfile } = useAuth();
  const memberOrderList = fevent.memberOrderList;
  const navigate = useNavigate();
  //* 참조 고정 필수: 렌더마다 새 배열이면 아래 시딩 effect가 매 렌더 실행되고,
  //* useWatch(memberBugo) 구독 때문에 setValue→리렌더→시딩이 무한 순환한다
  const memberHasPhoneNumberList = useMemo(
    () => memberOrderList?.filter((member) => member?.phoneNumber),
    [memberOrderList],
  );

  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  //* 전송 대상 체크 상태 (memberId -> 사용자가 직접 토글한 값, 없으면 자동 판정)
  //* 폼 시딩이 아닌 로컬 상태라 refetch에 덮어써지지 않음
  const [sendCheckOverrides, setSendCheckOverrides] = useState<Record<string, boolean>>(
    {},
  );
  const watchedMemberBugo = useWatch({ control, name: 'memberBugo' });

  //* 한 번도 전송된 적 없는 상주
  const isUnsentMember = useCallback(
    (member: Member<string, Bugo, CashAccount>) => !member.bugo?.lastSentPhoneNumber,
    [],
  );

  //* 미전송이거나 마지막 전송번호와 현재 번호가 다른 상주(=수정·추가된 상주)
  const isEditedOrAddedMember = useCallback(
    (member: Member<string, Bugo, CashAccount>, index: number) => {
      const currentPhone = watchedMemberBugo?.[index]?.phoneNumber ?? member.phoneNumber;
      const lastSent = member.bugo?.lastSentPhoneNumber;
      return !lastSent || lastSent !== currentPhone;
    },
    [watchedMemberBugo],
  );

  //* 기본 체크 = 수정·추가된 상주, 사용자가 직접 토글하면 오버라이드 우선
  const isMemberSendChecked = useCallback(
    (member: Member<string, Bugo, CashAccount>, index: number) => {
      const override = sendCheckOverrides[member._id];
      if (override !== undefined) return override;
      return isEditedOrAddedMember(member, index);
    },
    [sendCheckOverrides, isEditedOrAddedMember],
  );

  const onToggleSendCheck = useCallback((memberId: string, next: boolean) => {
    setSendCheckOverrides((prev) => ({ ...prev, [memberId]: next }));
  }, []);

  //* 퀵 선택: 조건에 맞는 상주만 체크되도록 일괄 세팅
  const applySendCheckPreset = useCallback(
    (preset: (member: Member<string, Bugo, CashAccount>, index: number) => boolean) => {
      const overrides: Record<string, boolean> = {};
      memberHasPhoneNumberList.forEach((member, index) => {
        overrides[member._id] = preset(member, index);
      });
      setSendCheckOverrides(overrides);
    },
    [memberHasPhoneNumberList],
  );

  //* 현재 체크 상태가 해당 퀵 선택 결과와 일치하는지 (칩 활성 표시용)
  const isPresetActive = useCallback(
    (preset: (member: Member<string, Bugo, CashAccount>, index: number) => boolean) =>
      memberHasPhoneNumberList.every(
        (member, index) => isMemberSendChecked(member, index) === preset(member, index),
      ),
    [memberHasPhoneNumberList, isMemberSendChecked],
  );

  const sendCheckedCount = useMemo(
    () =>
      memberHasPhoneNumberList.filter((member, index) =>
        isMemberSendChecked(member, index),
      ).length,
    [memberHasPhoneNumberList, isMemberSendChecked],
  );

  const allSendChecked =
    memberHasPhoneNumberList.length > 0 &&
    sendCheckedCount === memberHasPhoneNumberList.length;

  const onToggleAllSendCheck = useCallback(() => {
    const next = !allSendChecked;
    const overrides: Record<string, boolean> = {};
    memberHasPhoneNumberList.forEach((member) => {
      overrides[member._id] = next;
    });
    setSendCheckOverrides(overrides);
  }, [allSendChecked, memberHasPhoneNumberList]);

  const queryClient = useQueryClient();
  const { isLoading: sendingBugo, mutateAsync: mutateSendAsync } = useMutation(
    defaultApiMutationOptions,
    {
      onSuccess: () => {
        if (fevent) {
          notificateSuccess(notifiacationInstance, '부고안내 전송 완료');
          //* 전송 후엔 refetch된 lastSentPhoneNumber 기준으로 체크 상태 재판정
          setSendCheckOverrides({});
          queryClient.invalidateQueries(feventHookUrl(fevent._id, bugoRole()));
        }
      },
    },
  );

  // _id 세팅
  useEffect(() => {
    memberHasPhoneNumberList.map((member, index) => {
      setValue(`memberBugo.${index}._id`, member._id);
      setValue(`memberBugo.${index}.bugo`, member.bugo);
      setValue(`memberBugo.${index}.bugoId`, member.bugo?._id);
      setValue(`memberBugo.${index}.mournerText`, member.bugo?.mournerText);
      setValue(`memberBugo.${index}.memberId`, member._id);
      setValue(`memberBugo.${index}.phoneNumber`, member.phoneNumber);
      setValue(`memberBugo.${index}.hiddenMemberIds`, member.bugo?.hiddenMemberIds ?? []);
    });
  }, [memberHasPhoneNumberList, setValue]);

  const getBugoIdListAfterUpdateBugo = async (
    memberBugoIFormInputs: MemberBugoIFormInput[],
    sendTargetMemberIds?: Set<string>,
  ) => {
    /*
    * member 별로
    0. bugo가 있는지 확인
    1. bugo가 없다면 bugo 생성
    2. bugo가 있다면 update 여부 확인 후 bugo 업데이트
    3. sendTargetMemberIds가 주어지면 체크된 멤버의 bugoId만 반환(나머지는 저장만)
     */
    if (userProfile) {
      const bugoIdList = await Promise.all(
        memberBugoIFormInputs.map(async (memberBugoInput) => {
          if (memberBugoInput.memberId) {
            const memberUpdate = {
              _id: memberBugoInput.memberId,
              phoneNumber: memberBugoInput.phoneNumber,
            };
            await updateMember(memberUpdate, bugoRole());
          }

          let bugoId = memberBugoInput.bugoId;
          if (memberBugoInput.bugoId) {
            const bugoUpdate = {
              _id: memberBugoInput.bugoId,
              mournerText: memberBugoInput[MemberBugoSendFormInputsEnum.MournerText],
              hiddenMemberIds: memberBugoInput.hiddenMemberIds ?? [],
              //* 관리자(Admin) 대리 작업일 때만 상가(fevent) 귀속으로 정렬 (오귀속 교정)
              ...bugoAttributionForUpdate(fevent, userProfile),
            };
            await updateBugo(bugoUpdate, bugoRole());
          } else {
            const bugoCreate = {
              //* 관리자(Admin)가 대신 전송할 때만 상가의 담당 지도사·브랜드에 귀속
              //* (일반 role은 기존 그대로 로그인 계정 귀속)
              ...bugoAttributionForCreate(fevent, userProfile),
              fevent: fevent._id,
              member: memberBugoInput._id,
              mournerText: memberBugoInput[MemberBugoSendFormInputsEnum.MournerText],
              hiddenMemberIds: memberBugoInput.hiddenMemberIds ?? [],
              schedule: {
                courtesySend: false,
              },
              config: {
                isEnable: true,
              },
            };
            const bugo = await createBugo(bugoCreate, bugoRole());
            bugoId = bugo._id;
          }
          if (sendTargetMemberIds && !sendTargetMemberIds.has(memberBugoInput._id)) {
            return null;
          }
          return bugoId;
        }),
      );
      return bugoIdList;
    }
  };

  //* 체크된 상주에게 부고 발송 (이미 전송완료여도 체크되어 있으면 재전송)
  const sendBugoFunc = async (memberBugoIFormInputs: MemberBugoIFormInput[]) => {
    const sendTargetMemberIds = new Set(
      memberHasPhoneNumberList
        .filter((member, index) => isMemberSendChecked(member, index))
        .map((member) => member._id),
    );
    const bugoIdList = await getBugoIdListAfterUpdateBugo(
      memberBugoIFormInputs,
      sendTargetMemberIds,
    );
    if (bugoIdList) {
      const bugoIds = bugoIdList.filter((item) => {
        if (item) {
          return true;
        } else return false;
      }) as string[];
      const result = await sendBugo(bugoIds, bugoRole());
      console.log('message', result);
    }
  };

  const onSubmit: SubmitHandler<MemberBugoIFormInputs> = async (data) => {
    await mutateSendAsync(sendBugoFunc(data.memberBugo));
  };

  //* 부고 전송 전 고인정보 확인 모달
  const confirmOpenId = `sendBugo-confirm-${fevent._id}`;
  const setConfirmModalOpen = useSetRecoilState(simpleOpenAtomFamily(confirmOpenId));
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({
    name: false,
    sex: false,
    deathDate: false,
  });

  const allChecked = checkedItems.name && checkedItems.sex && checkedItems.deathDate;

  const handleCheckChange = useCallback((key: string) => {
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const resetChecks = useCallback(() => {
    setCheckedItems({ name: false, sex: false, deathDate: false });
  }, []);

  const deathDateDisplay = useMemo(() => {
    const death = fevent.deceasedInfo?.death;
    if (death?.date) {
      return formatFeventDateString(death, false, true);
    }
    return '미정';
  }, [fevent.deceasedInfo?.death]);

  const previewOpenId = `sendBugo-preview-${fevent._id}`;
  const setPreviewModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(`sendBugo-preview-${fevent._id}`),
  );

  //* 미리보기 클릭 -> 기존에 저장해둔 설정 다 저장(onSubmit) 후 해당 bugoId로 navigate
  const bugoPreviewMemberClick = async (
    index: number,
    memberBugoIFormInputs: MemberBugoIFormInput[],
  ) => {
    const bugoIdList = await getBugoIdListAfterUpdateBugo(memberBugoIFormInputs);
    const bugoId = bugoIdList && bugoIdList[index];
    if (bugoId) {
      navigate(`/bugo/${bugoIdList[index]}/preview`);
    } else {
      // console.log('error bugo preview');
    }
  };

  const headerText = useMemo(() => {
    if (memberHasPhoneNumberList.length > 0) {
      return (
        <div className="ml-2 mb-3 text-sm font-bold">
          <p>상주님께 부고를 전송합니다</p>
          <p className="text-12 font-medium">
            체크된 상주님께 카톡, 문자 둘다 전송됩니다
          </p>
        </div>
      );
    } else {
      return (
        <div className="break-keep text-center text-sm font-medium">
          <p>휴대번호가 존재하는 상주님이 존재하지 않습니다</p>
          <p>
            아래 <span className="font-bold">상주정보 수정 버튼</span>을 클릭하여 주세요
          </p>
        </div>
      );
    }
  }, [memberHasPhoneNumberList.length]);

  return (
    <div>
      {headerText}
      <form>
        {memberHasPhoneNumberList.length > 1 && (
          <div className="mb-2 flex flex-wrap items-center justify-end gap-1.5 text-xs font-bold">
            <button
              type="button"
              onClick={() => applySendCheckPreset(isUnsentMember)}
              className={`rounded-full border px-2.5 py-1 transition-colors ${
                isPresetActive(isUnsentMember)
                  ? 'theme-border-main theme-text-main'
                  : 'theme-border-1 theme-text-8'
              }`}
            >
              미전송만
            </button>
            <button
              type="button"
              onClick={() => applySendCheckPreset(isEditedOrAddedMember)}
              className={`rounded-full border px-2.5 py-1 transition-colors ${
                isPresetActive(isEditedOrAddedMember)
                  ? 'theme-border-main theme-text-main'
                  : 'theme-border-1 theme-text-8'
              }`}
            >
              수정·추가만
            </button>
            <button
              type="button"
              onClick={onToggleAllSendCheck}
              className={`rounded-full border px-2.5 py-1 transition-colors ${
                allSendChecked
                  ? 'theme-border-main theme-text-main'
                  : 'theme-border-1 theme-text-8'
              }`}
            >
              {allSendChecked ? '전체해제' : '전체선택'}
            </button>
          </div>
        )}
        <MemberBugoSender
          fevent={fevent}
          members={memberHasPhoneNumberList}
          register={register}
          control={control}
          setValue={setValue}
          isMemberSendChecked={isMemberSendChecked}
          onToggleSendCheck={onToggleSendCheck}
        />
        <div className="mt-8 space-y-4">
          <Button
            type="button"
            className="button-rectangle text-white theme-bg-13"
            onClick={() => {
              //Add mode 일때
              if (useMode === FeventFormUseMode.Add) {
                setSearchParams({ feventId: fevent._id, step: '0' });
              }
              // useMode Edit 또는 없을경우
              else {
                navigate(`/fevent/${fevent._id}/edit/?step=0`);
              }
            }}
          >
            부고장 수정
          </Button>

          <Button
            type="button"
            onClick={() => {
              resetChecks();
              setConfirmModalOpen(true);
            }}
            className="button-rectangle text-white theme-bg-main disabled:theme-text-3 disabled:theme-bg-10"
            disabled={
              memberHasPhoneNumberList?.length === 0 ||
              sendingBugo ||
              sendCheckedCount === 0
            }
          >
            {`${
              useMode === FeventFormUseMode.Add
                ? '상주님께 부고전송'
                : '체크된 상주님께 부고전송'
            }${sendCheckedCount > 0 ? ` (${sendCheckedCount}명)` : ''}`}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              className="button-rectangle text-white theme-bg-12"
              onClick={() => {
                setPreviewModalOpen(true);
              }}
            >
              부고장 미리보기
            </Button>
            <div>
              <Link to="/">
                <Button className="button-rectangle text-white theme-bg-12">
                  완료(홈으로)
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </form>

      <BottomPopup openId={previewOpenId}>
        <div className="w-screen rounded-t-2xl p-6 theme-text-9 theme-bg-1">
          {/* Title */}
          <h4 className="text-center text-sm font-bold">상주님 선택</h4>
          {/* Item List */}
          <ul className="max-h-64 overflow-y-auto pt-3 pb-4">
            {memberHasPhoneNumberList?.map((member, index) => (
              <li
                key={`select-${member?._id}`}
                onClick={(e) => {
                  handleSubmit((data) => {
                    const inputs = data.memberBugo;
                    bugoPreviewMemberClick(index, inputs);
                  })(e);
                }}
                className={`cursor-pointer border-b py-3 text-center leading-5 theme-border-1`}
              >
                {member?.fullName}
              </li>
            ))}
          </ul>
        </div>
      </BottomPopup>
      {/* 고인정보 확인 모달 */}
      <Modal openId={confirmOpenId} position="center">
        <div className="text-center">
          <h3 className="text-base font-bold">고인정보 확인</h3>
          <p className="mt-1 text-xs theme-text-9">
            전송 전 아래 정보가 정확한지 확인해주세요
          </p>

          <ul className="mt-5 space-y-3 text-left">
            <li
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors ${
                checkedItems.name
                  ? 'theme-border-main theme-bg-5'
                  : 'theme-bg-1 theme-border-1'
              }`}
              onClick={() => handleCheckChange('name')}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  checkedItems.name
                    ? 'text-white theme-border-main theme-bg-main'
                    : 'border-gray-300 text-gray-300'
                }`}
              >
                ✓
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold theme-text-9">고인 성함</p>
                <p className="mt-1 text-xl font-bold">
                  {fevent.deceasedInfo?.name || '-'}
                </p>
              </div>
            </li>

            <li
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors ${
                checkedItems.sex
                  ? 'theme-border-main theme-bg-5'
                  : 'theme-bg-1 theme-border-1'
              }`}
              onClick={() => handleCheckChange('sex')}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  checkedItems.sex
                    ? 'text-white theme-border-main theme-bg-main'
                    : 'border-gray-300 text-gray-300'
                }`}
              >
                ✓
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold theme-text-9">성별</p>
                <p className="mt-1 text-xl font-bold">
                  {fevent.deceasedInfo?.sex === Gender.Male
                    ? '남성'
                    : fevent.deceasedInfo?.sex === Gender.Femail
                    ? '여성'
                    : '-'}
                </p>
              </div>
            </li>

            <li
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 transition-colors ${
                checkedItems.deathDate
                  ? 'theme-border-main theme-bg-5'
                  : 'theme-bg-1 theme-border-1'
              }`}
              onClick={() => handleCheckChange('deathDate')}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                  checkedItems.deathDate
                    ? 'text-white theme-border-main theme-bg-main'
                    : 'border-gray-300 text-gray-300'
                }`}
              >
                ✓
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold theme-text-9">별세일</p>
                <p className="mt-1 text-xl font-bold">{deathDateDisplay}</p>
              </div>
            </li>
          </ul>

          {!allChecked && (
            <button
              type="button"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-bold transition-colors theme-border-main theme-text-main"
              onClick={() => setCheckedItems({ name: true, sex: true, deathDate: true })}
            >
              <span>✓</span>
              모두 확인
            </button>
          )}

          <div className="mt-5 grid grid-cols-2 gap-2">
            <Button
              type="button"
              className="button-rectangle text-white theme-bg-12"
              onClick={() => {
                setConfirmModalOpen(false);
                if (useMode === FeventFormUseMode.Add) {
                  setSearchParams({ feventId: fevent._id, step: '0' });
                } else {
                  navigate(`/fevent/${fevent._id}/edit/?step=0`);
                }
              }}
            >
              수정하기
            </Button>
            <Button
              type="button"
              className="button-rectangle text-white theme-bg-main disabled:opacity-40"
              disabled={!allChecked}
              onClick={(e) => {
                setConfirmModalOpen(false);
                handleSubmit(onSubmit)(e);
              }}
            >
              확인 후 전송
            </Button>
          </div>
        </div>
      </Modal>

      <LoadingModal open={sendingBugo} title="부고 전송중"></LoadingModal>
    </div>
  );
};

export default FeventSendBugoForm;
